/**
 * SurfaceShaders — TSL NodeMaterial shaders for lava, water, and wind
 *
 * Uses Three.js TSL (Three Shading Language) with auto-updating `time` node.
 * No manual time uniform updates needed — TSL handles it.
 */

import * as THREE from 'three/webgpu';
import {
  Fn, float, vec2, vec3, vec4,
  uv, positionLocal, positionWorld, time,
  sin, cos, floor, fract, dot, mix, smoothstep,
  uniform, normalView, positionViewDirection,
  max, min, pow, abs, oneMinus, length, fwidth
} from 'three/tsl';

// ─── Shared Noise (TSL) ────────────────────────────────────

const hash2d = Fn(([p_immutable]) => {
  const p = vec2(p_immutable).toVar();
  return fract(sin(dot(p, vec2(127.1, 311.7))).mul(43758.5453123));
});

const noise2d = Fn(([p_immutable]) => {
  const p = vec2(p_immutable).toVar();
  const i = floor(p);
  const f = fract(p);
  const ff = f.mul(f).mul(f.mul(-2.0).add(3.0));
  const a = hash2d(i);
  const b = hash2d(i.add(vec2(1.0, 0.0)));
  const c = hash2d(i.add(vec2(0.0, 1.0)));
  const d = hash2d(i.add(vec2(1.0, 1.0)));
  return mix(mix(a, b, ff.x), mix(c, d, ff.x), ff.y);
});

// ─── Arena deck ─────────────────────────────────────────────

/**
 * The ground the match is actually played on.
 *
 * The instanced ground slab stretches one 0..1 face UV over 90 units, which
 * turned the shared brick texture into blurred 20-unit tiles — no scale cue,
 * no way to judge a jump against it. This material works in WORLD units off
 * `positionWorld`, so a grid cell is the same size no matter what mesh wears
 * it: v1's GridHelper language (fine cells + stronger majors on a dark ground)
 * rebuilt as TSL, because GridHelper itself is a legacy-material crash under
 * WebGPURenderer.
 *
 * Lit (toon) rather than unlit so platform shadows still land on the ground —
 * the shadows are half the depth read on a graybox.
 */
// Module-level: one gradient for every deck the session builds, so per-match
// arena rebuilds never leak it. Local rather than ToonMaterials' GRADIENT_3 —
// ToonMaterials imports from this file, so importing back would be a cycle.
let _deckGradient = null;
function deckGradient() {
  if (_deckGradient) return _deckGradient;
  _deckGradient = new THREE.DataTexture(new Uint8Array([0, 128, 255]), 3, 1, THREE.RedFormat);
  _deckGradient.magFilter = THREE.NearestFilter;
  _deckGradient.minFilter = THREE.NearestFilter;
  _deckGradient.needsUpdate = true;
  return _deckGradient;
}

export function createDeckMaterial({ arenaRadius, centerX = 0, centerZ = 0, colorShift = null }) {
  const material = new THREE.MeshToonNodeMaterial({ color: 0xffffff, gradientMap: deckGradient() });

  /*
   * BIG PANELS, NOT A GRID.
   *
   * This drew a fine 3-unit grid with stronger majors — a graybox floor, and
   * legible, but not what `docs/reference/chart.jpg` shows. The reference deck
   * is a raised access floor: large plates about four units across, separated
   * by a thick dark seam, with a bolt at each corner. That is the same surface
   * `floorPanelTexture` gives the platforms, so drawing it here too is what
   * makes floor and furniture read as one room instead of two materials that
   * happen to share a hue.
   *
   * And it is DARK. In the reference the deck is the darkest large surface and
   * the candles are the brightest thing standing on it; ours had them at nearly
   * the same value, which is why nothing popped and the arena read as flat.
   * Value contrast is doing more work here than colour is.
   */
  const PANEL = 4.4;
  const SEAM = 0.16;
  const BOLT_INSET = 0.62;

  material.colorNode = Fn(() => {
    const px = positionWorld.x.sub(centerX);
    const pz = positionWorld.z.sub(centerZ);
    const r = length(vec2(px, pz));

    // HUE AND SATURATION ONLY. The theme's lightness term is deliberately
    // dropped here.
    //
    // A theme describes the arena's IDENTITY, which is a hue and how saturated
    // it is. Lightness is not an arena property, it is a SURFACE ROLE: the deck
    // is the dark ground and the platforms are the bright things standing on
    // it, which is the value relationship the reference has and the reason its
    // candles read as objects.
    //
    // Applying the same lightness shift to both is exactly what flattened the
    // first attempt — brightening the theme to lift the candles lifted the deck
    // with them and the contrast never changed. The deck's darkness lives in
    // the constants below, where nothing can accidentally cancel it.
    //
    // Resolved in JS rather than TSL: a handful of constants computed once at
    // build time instead of per pixel.
    const tint = (cr, cg, cb) => {
      const c = new THREE.Color(cr, cg, cb);
      if (colorShift) c.offsetHSL(colorShift[0], colorShift[1], 0);
      return vec3(c.r, c.g, c.b);
    };
    // Roughly half the old values. The toon ramp and the key light lift a big
    // upward-facing surface a long way, so a deck authored at "looks right in
    // the swatch" renders pale.
    const plate = tint(0.075, 0.115, 0.155);
    const plateAlt = tint(0.06, 0.095, 0.13);
    const seamCol = tint(0.028, 0.042, 0.06);
    const boltCol = tint(0.2, 0.27, 0.34);

    // Cell-local coordinates, centred, in world units.
    const cell = vec2(px, pz).div(PANEL);
    const cellId = floor(cell);
    const local = fract(cell).sub(0.5).mul(PANEL);
    const ax = abs(local.x);
    const az = abs(local.y);

    // Distance inward from the plate's edge, so the seam is the gap BETWEEN
    // plates rather than a line drawn on top of one.
    const edgeDist = float(PANEL * 0.5).sub(max(ax, az));
    const aa = fwidth(px).mul(1.5).add(0.01);
    const seam = oneMinus(smoothstep(float(SEAM), aa.add(SEAM), edgeDist));

    // One bolt per corner: distance to the nearest inset corner point.
    const bi = float(PANEL * 0.5 - BOLT_INSET);
    const boltD = length(vec2(ax.sub(bi), az.sub(bi)));
    const bolt = oneMinus(smoothstep(float(0.075), float(0.13), boltD));

    // Alternate plates and mottle them. A floor of identical plates reads as a
    // texture repeat; this is what stops the eye finding the tiling.
    const checker = cellId.x.add(cellId.y).mod(2.0);
    const mottle = noise2d(vec2(px, pz).mul(0.05)).mul(0.16).add(0.92);

    let color = mix(plate, plateAlt, checker).mul(mottle).toVar();
    color.assign(mix(color, seamCol, seam));
    // Bolts sit on the plate, never in the seam.
    color.assign(mix(color, boltCol, bolt.mul(oneMinus(seam))));

    // Beyond the zone's start radius the world is over; sink the deck toward
    // the fog colour so the rim dissolves instead of ending on a hard edge.
    const edge = smoothstep(float(arenaRadius * 1.02), float(arenaRadius * 1.45), r);
    return mix(color, vec3(0.05, 0.055, 0.115), edge);
  })();

  return material;
}

// ─── Chaos floor overlays ───────────────────────────────────

/**
 * Materials for the three floor mutators (ice / conveyor / lava).
 *
 * A mutator changes how the ground BEHAVES the tick it lands; these are how it
 * changes what the ground LOOKS like the same frame. One material per kind for
 * the whole session, shared by the arena circle and every platform quad, so an
 * activation costs zero pipeline compiles after the first and the fade uniform
 * drives everything in step. `strength` is owned by ArenaRenderer's ticker.
 */
const _chaosMats = {};

export function getChaosOverlayMaterials() {
  if (_chaosMats.ice) return _chaosMats;

  const strength = uniform(0);
  _chaosMats.strength = strength;
  // Set per map by ArenaRenderer: the overlays fade out at the same radius the
  // deck does, or they hang as a glowing ring over the dissolved rim.
  const radius = uniform(45);
  _chaosMats.radius = radius;
  const rimFade = (p) => oneMinus(smoothstep(radius, radius.mul(1.3), length(p)));

  // ICE — pale glaze with drifting glints. Reads as "slick" without hiding
  // the deck grid under it.
  {
    const material = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false });
    const p = vec2(positionWorld.x, positionWorld.z);
    const glint = smoothstep(0.88, 0.99, noise2d(p.mul(0.8).add(time.mul(0.12))));
    const streak = noise2d(p.mul(0.08).sub(time.mul(0.03))).mul(0.12);
    material.colorNode = vec3(0.5, 0.78, 1.0).add(glint.mul(0.5));
    material.opacityNode = float(0.2).add(streak).add(glint.mul(0.3)).mul(strength).mul(rimFade(p));
    material.fog = true;
    _chaosMats.ice = material;
  }

  // CONVEYOR — chevrons marching along the drift direction. The apex of each
  // V points where the floor is taking you.
  {
    const dir = uniform(new THREE.Vector2(1, 0));
    const speed = uniform(1);
    const material = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false });
    const p = vec2(positionWorld.x, positionWorld.z);
    const along = dot(p, dir);
    const across = abs(dot(p, vec2(dir.y.negate(), dir.x)));
    const c = fract(along.mul(0.22).sub(time.mul(speed).mul(0.5)).add(across.mul(0.13)));
    const band = smoothstep(0.52, 0.6, c).mul(smoothstep(0.84, 0.72, c));
    material.colorNode = vec3(1.0, 0.55, 0.12);
    material.opacityNode = band.mul(0.34).add(0.04).mul(strength).mul(rimFade(p));
    material.fog = true;
    _chaosMats.conveyor = material;
    _chaosMats.conveyorDir = dir;
    _chaosMats.conveyorSpeed = speed;
  }

  // LAVA — the fragment half of createLavaShaderMaterial in world units.
  // No vertex displacement: on a -PI/2-rotated plane, local-Y displacement is
  // a WORLD-Z slide, and the overlay must hug the deck exactly.
  {
    const material = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false });
    const p = vec2(positionWorld.x, positionWorld.z);
    const n1 = noise2d(p.mul(0.12).add(time.mul(0.3)));
    const n2 = noise2d(p.mul(0.24).sub(time.mul(0.5)));
    const combined = n1.mul(0.6).add(n2.mul(0.4));
    const darkRed = vec3(0.5, 0.05, 0.0);
    const orange = vec3(0.9, 0.3, 0.0);
    const yellow = vec3(1.0, 0.8, 0.2);
    let color = mix(darkRed, orange, smoothstep(0.2, 0.5, combined));
    color = mix(color, yellow, smoothstep(0.6, 0.85, combined));
    const crack = smoothstep(0.78, 0.82, combined);
    color = color.add(vec3(1.0, 0.9, 0.4).mul(crack).mul(0.8));
    material.colorNode = color.mul(float(0.9).add(sin(time.mul(2.0)).mul(0.1)));
    material.opacityNode = float(0.92).mul(strength).mul(rimFade(p));
    material.fog = true;
    _chaosMats.lava = material;
  }

  return _chaosMats;
}

// ─── Per-frame hooks ────────────────────────────────────────

/**
 * main.js calls `updateConveyorScrolls` every render frame; that call is the
 * one clock this module is guaranteed to receive. Arena-side animations (the
 * chaos overlay fades) register here instead of adding another main.js hook.
 */
const _frameHooks = new Set();

export function registerSurfaceFrameHook(fn) {
  _frameHooks.add(fn);
  return () => _frameHooks.delete(fn);
}

// ─── Platform rims ──────────────────────────────────────────

const _rimMaterialCache = new Map();

/**
 * Unlit border line for a raised platform's top face.
 *
 * A graybox platform is a dark toon box whose side faces sit in the deepest
 * gradient band — from eye height its silhouette is a black slab against a
 * black slab, and there is no line telling you where the landable top ends.
 * The rim is that line. Unlit on purpose: it has to read at the far side of
 * the arena, where a lit line would fall into the same shadow as the box.
 *
 * Built from `positionLocal` (the quad is authored at world scale) so the
 * border is a constant 0.22 units on any footprint; cached by quantized size
 * because each distinct footprint bakes its half-extents into the node graph.
 */
export function createPlatformRimMaterial(halfX, halfZ) {
  const key = `${halfX.toFixed(2)}|${halfZ.toFixed(2)}`;
  const cached = _rimMaterialCache.get(key);
  if (cached) return cached;

  const material = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false });

  // An EDGE, not a border.
  //
  // At 0.22 wide and 0.85 opaque this was a thick white frame inset into every
  // platform top — from the match camera it read as a sticker applied to each
  // box rather than as light catching a corner. The reference has a bright
  // edge one or two pixels wide where the top face meets the side, and nothing
  // else; the top face itself is simply lit.
  //
  // It also scales with the platform now. A fixed 0.22 is a third of a 1.5-wide
  // candle's half-width and a twentieth of an island's, so the same constant
  // read as a hairline on one and a painted margin on the other.
  const BORDER = Math.min(0.09, Math.min(halfX, halfZ) * 0.14);
  material.colorNode = vec3(0.62, 0.86, 0.98);
  material.opacityNode = Fn(() => {
    const edgeX = float(halfX).sub(abs(positionLocal.x));
    const edgeZ = float(halfZ).sub(abs(positionLocal.y));
    const d = min(edgeX, edgeZ);
    const aa = fwidth(d).add(0.01);
    return oneMinus(smoothstep(float(BORDER), float(BORDER).add(aa), d)).mul(0.55);
  })();

  _rimMaterialCache.set(key, material);
  return material;
}

export function clearPlatformRimMaterials() {
  for (const mat of _rimMaterialCache.values()) mat.dispose();
  _rimMaterialCache.clear();
}

// ─── Lava ───────────────────────────────────────────────────

export function createLavaShaderMaterial() {
  const material = new THREE.NodeMaterial();
  material.transparent = true;
  material.side = THREE.DoubleSide;

  // Vertex: displacement via noise
  const pos = positionLocal.toVar();
  const n = noise2d(pos.xz.mul(0.3).add(time.mul(0.4))).mul(0.4)
    .add(noise2d(pos.xz.mul(0.7).sub(time.mul(0.3))).mul(0.2));
  material.positionNode = pos.add(vec3(0, n, 0));

  // Fragment: color ramp
  const uvScaled = uv().mul(6.0);
  const n1 = noise2d(uvScaled.add(time.mul(0.3)));
  const n2 = noise2d(uvScaled.mul(2.0).sub(time.mul(0.5)));
  const combined = n1.mul(0.6).add(n2.mul(0.4));

  const darkRed = vec3(0.5, 0.05, 0.0);
  const orange = vec3(0.9, 0.3, 0.0);
  const yellow = vec3(1.0, 0.8, 0.2);
  let color = mix(darkRed, orange, smoothstep(0.2, 0.5, combined));
  color = mix(color, yellow, smoothstep(0.6, 0.85, combined));

  // Bright crack lines
  const crack = smoothstep(0.78, 0.82, combined);
  color = color.add(vec3(1.0, 0.9, 0.4).mul(crack).mul(0.8));

  // Pulsing glow
  color = color.mul(float(0.9).add(sin(time.mul(2.0)).mul(0.1)));

  material.colorNode = color;
  material.opacityNode = float(0.9);

  return material;
}

// ─── Water ──────────────────────────────────────────────────

export function createWaterShaderMaterial() {
  const material = new THREE.NodeMaterial();
  material.transparent = true;
  material.side = THREE.DoubleSide;

  // Vertex: multi-frequency sine waves
  const pos = positionLocal.toVar();
  const wave = sin(pos.x.mul(1.5).add(time.mul(2.0))).mul(0.15)
    .add(sin(pos.z.mul(2.0).sub(time.mul(1.5))).mul(0.1))
    .add(sin(pos.x.add(pos.z).mul(0.8).add(time)).mul(0.08));
  material.positionNode = pos.add(vec3(0, wave, 0));

  // Fragment: Fresnel + shimmer
  const fresnel = pow(oneMinus(max(dot(normalView, positionViewDirection), 0.0)), 2.0);

  const deepBlue = vec3(0.05, 0.15, 0.4);
  const surfaceTeal = vec3(0.1, 0.5, 0.6);
  const highlight = vec3(0.4, 0.8, 0.9);

  const uvCoord = uv();
  let color = mix(deepBlue, surfaceTeal, uvCoord.y.mul(0.5).add(0.5));

  // Shimmer
  const shimmer = sin(uvCoord.x.mul(20.0).add(time.mul(3.0))).mul(sin(uvCoord.y.mul(15.0).sub(time.mul(2.0))));
  color = color.add(highlight.mul(smoothstep(0.7, 1.0, shimmer)).mul(0.3));

  const alpha = mix(float(0.6), float(0.85), fresnel);

  material.colorNode = color;
  material.opacityNode = alpha;

  return material;
}

// ─── Wind ───────────────────────────────────────────────────

export function createWindShaderMaterial(windForce = [1, 0, 0]) {
  const material = new THREE.NodeMaterial();
  material.transparent = true;
  material.side = THREE.DoubleSide;
  material.depthWrite = false;
  material.blending = THREE.AdditiveBlending;

  const windDir = uniform(new THREE.Vector3(...windForce).normalize());

  // Fragment: scrolling dashes in wind direction
  const uvCoord = uv();
  const dir = windDir.xz.normalize();
  const projected = dot(uvCoord.sub(0.5), dir);
  const dashRaw = sin(projected.mul(15.0).sub(time.mul(4.0)).mul(3.14159)).mul(0.5).add(0.5);
  const dash = smoothstep(0.6, 0.8, dashRaw);

  // Fade at edges
  const edge = smoothstep(0.0, 0.15, uvCoord.x)
    .mul(smoothstep(1.0, 0.85, uvCoord.x))
    .mul(smoothstep(0.0, 0.15, uvCoord.y))
    .mul(smoothstep(1.0, 0.85, uvCoord.y));

  const windColor = vec3(0.6, 0.85, 1.0);
  const windAlpha = dash.mul(edge).mul(0.15);

  material.colorNode = windColor;
  material.opacityNode = windAlpha;

  return material;
}

// ─── Conveyor (CPU-based, unchanged) ────────────────────────

const conveyorMaterials = [];

export function registerConveyorMaterial(material, speed, direction) {
  conveyorMaterials.push({ material, speed, direction });
}

export function updateConveyorScrolls(delta) {
  for (const entry of conveyorMaterials) {
    if (entry.material.map) {
      const dir = entry.direction || [1, 0, 0];
      entry.material.map.offset.x += dir[0] * entry.speed * delta * 0.1;
      entry.material.map.offset.y += dir[2] * entry.speed * delta * 0.1;
    }
  }
  for (const fn of _frameHooks) fn(delta);
}

/**
 * Drops the registry when an arena is torn down.
 *
 * v1 never needed this — one client, one arena, one page load. v2 rebuilds the
 * arena per match, and without this the list grows by every conveyor in every
 * map the session ever loaded, animating textures nothing is drawing.
 */
export function clearConveyorMaterials() {
  conveyorMaterials.length = 0;
}
